/// <reference path="../../typings.d.ts" />

import type { IFrameType, IIframeCommand, CommandListenerType } from './iframe-client.typings';

const MADRID_META_KEY = Symbol.for('MADRID_META_KEY');
const MADRID_SHARED_STATE_KEY = Symbol.for('MADRID_SHARED_STATE_KEY');
const MADRID_COMMAND_LISTENER_KEY = Symbol.for('MADRID_COMMAND_LISTENER_KEY');
const MADRID_COMMAND_KEY = Symbol.for('MADRID_COMMAND_KEY');
export const SHARED_SERVICE_INJECTIONS = Symbol.for('SHARED_SERVICE_INJECTIONS');

let theModuleIdMap: Map<string | number, number> | undefined;

interface IFrameMeta {
    iframeType: IFrameType;
    iframeId: string;
}

export function ensureIframeMeta(): IFrameMeta {
    if (!Reflect.get(window, MADRID_META_KEY)) {
        loadMeta();
    }
    return Reflect.get(window, MADRID_META_KEY) || {};
}

export function ensureSharedServiceInstances() {
    if (!Reflect.get(getSharedState(), SHARED_SERVICE_INJECTIONS)) {
        Reflect.defineProperty(getSharedState(), SHARED_SERVICE_INJECTIONS, {
            value: {},
            writable: false,
            enumerable: true,
            configurable: false
        });
    }
    return Reflect.get(getSharedState(), SHARED_SERVICE_INJECTIONS);
}

export function isTridentIframeType(expectedType: IFrameType | IFrameType[]): boolean {
    const { iframeType } = ensureIframeMeta();
    return Array.isArray(expectedType) ? expectedType.includes(iframeType) : expectedType === iframeType;
}

function getSharedModuleKey(moduleId: string | number): string {
    return `__sharedModule${moduleId}`;
}

function loadMeta(): void {
    const url = new URL(window.location.href);
    if (url.searchParams.get('iframeType')) {
        Reflect.defineProperty(window, MADRID_META_KEY, {
            get: () =>
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                ({
                    iframeType: url.searchParams.get('iframeType'),
                    iframeId: url.searchParams.get('iframeId')
                } as IFrameMeta),
            enumerable: false,
            configurable: false
        });
    }
}

export function getSharedState(): Record<string, unknown> {
    let workerIframe: Window = null;

    // iframe hierarchy: Teams frame(if loaded in Teams) -> shell frame -> DPE worker/page/dialog/panel frame -> Madrid worker/page/dialog/panel frame
    Array.prototype.forEach.call(window.parent.parent.frames, (dpeFrame: Window) => {
        Array.prototype.forEach.call(dpeFrame.frames, (madridFrame: Window) => {
            try {
                const meta = Reflect.get(madridFrame, MADRID_META_KEY) as IFrameMeta;
                if (meta.iframeType === 'worker') {
                    if (workerIframe) {
                        throw new Error('There should be only one worker iframe');
                    }
                    workerIframe = madridFrame;
                }
            } catch (e) {
                /** no-op */
            }
        });
    });

    if (!workerIframe) {
        throw new Error('Cannot find the worker iframe');
    }

    if (!Reflect.get(workerIframe, MADRID_SHARED_STATE_KEY)) {
        Reflect.defineProperty(workerIframe, MADRID_SHARED_STATE_KEY, {
            value: {},
            writable: false,
            enumerable: false,
            configurable: false
        });
    }

    return Reflect.get(workerIframe, MADRID_SHARED_STATE_KEY);
}

// eslint-disable-next-line
export function madridRuntimePreloadWorker(moduleId: string | number, module: any): void {
    theModuleIdMap = theModuleIdMap ?? new Map(globalThis.hashedModuleIdMap);
    if (theModuleIdMap.has(moduleId)) {
        const sharedState = getSharedState();
        const theKey = getSharedModuleKey(theModuleIdMap.get(moduleId));
        if (!sharedState[theKey]) {
            sharedState[theKey] = module;
        }
    }
}

export function madridRuntimePreload(moduleId: string | number): unknown {
    theModuleIdMap = theModuleIdMap ?? new Map(globalThis.hashedModuleIdMap);
    if (theModuleIdMap.has(moduleId)) {
        const sharedState = getSharedState();
        const theKey = getSharedModuleKey(theModuleIdMap.get(moduleId));
        return sharedState[theKey];
    }

    return undefined;
}

function getCommandLister(): Record<IFrameType, CommandListenerType> {
    const sharedState = getSharedState();
    if (!Reflect.get(sharedState, MADRID_COMMAND_LISTENER_KEY)) {
        Object.defineProperty(sharedState, MADRID_COMMAND_LISTENER_KEY, {
            value: {
                worker: () => {},
                page: () => {},
                panel: () => {},
                dialog: () => {}
            },
            writable: false,
            enumerable: false,
            configurable: false
        });
    }
    return Reflect.get(sharedState, MADRID_COMMAND_LISTENER_KEY);
}

function registerCommandListener(iframeType: IFrameType, listener: CommandListenerType): void {
    const commandListener = getCommandLister();
    commandListener[iframeType] = listener;
}

function getPendingCommand<D, T>(): Array<IIframeCommand<D, T>> {
    const sharedState = getSharedState();
    if (!Reflect.get(sharedState, MADRID_COMMAND_KEY)) {
        Reflect.defineProperty(sharedState, MADRID_COMMAND_KEY, {
            value: [],
            writable: false,
            enumerable: false,
            configurable: false
        });
    }
    return Reflect.get(sharedState, MADRID_COMMAND_KEY);
}

export function queueCommand<D, T = unknown>(command: IIframeCommand<D, T>): Promise<T> {
    const pendingCommand = getPendingCommand();
    const commandListener = getCommandLister();
    pendingCommand.push(command);
    return new Promise<T>((resolve, reject) => {
        command.resolve = resolve;
        command.reject = reject;
        commandListener[command.preparer || command.executor]();
    });
}

async function executeIframeCommand<D, T>(
    executePreparerCommand: (currentExecutor: IFrameType, command: IIframeCommand<D, T>) => Promise<T>,
    executeCommand: (currentExecutor: IFrameType, command: IIframeCommand<D, T>) => Promise<T>
): Promise<void> {
    let resolve;
    let reject;
    try {
        const { iframeType } = ensureIframeMeta();
        const pendingCommand = getPendingCommand<D, T>();

        let command: IIframeCommand<D, T> = null;
        let index: number = null;
        let commandResult: T;
        pendingCommand.forEach((c, idx) => {
            if (c.preparer === iframeType || c.executor === iframeType) {
                command = c;
                index = idx;
            }
        });

        if (!command) {
            return;
        }

        pendingCommand.splice(index, 1);
        resolve = command.resolve;
        reject = command.reject;

        if (command.preparer) {
            const currentExecutor = command.preparer;
            delete command.preparer;
            await executePreparerCommand(currentExecutor, command);
            commandResult = await queueCommand<D, T>(command);
        } else {
            commandResult = await executeCommand(command.executor, command);
        }
        if (resolve) {
            resolve(commandResult);
        }
    } catch (e) {
        if (reject) {
            reject(e);
        } else {
            throw e;
        }
    }
}

export function listenToCommand(
    executePreparerCommand: (currentExecutor: IFrameType, command: IIframeCommand) => Promise<void>,
    executeCommand: (currentExecutor: IFrameType, command: IIframeCommand) => Promise<unknown>
): void {
    const { iframeType } = ensureIframeMeta();
    registerCommandListener(iframeType, async () => {
        executeIframeCommand(executePreparerCommand, executeCommand);
    });
    // cleanup the pending commands pushed before iframe initialization
    executeIframeCommand(executePreparerCommand, executeCommand);
}
